import clsx from 'clsx';
import { CATEGORY_LABEL } from '@/lib/circulars';
import { formatDate } from '@/lib/formatDate';
import { formatSize } from '@/lib/formatSize';
import type { Circular } from '@/lib/types';

const TITLE = 'min-w-0 text-[1.125rem] leading-[1.4] font-medium';
const NEW_BADGE = 'ml-2 bg-teal-700 px-1.5 py-0.5 align-middle font-mono text-[0.75rem] text-white';

interface Props {
  circular: Circular;
  /** 'compact' is the Home card; 'full' is the Notices page (edge to edge, row highlights on hover). */
  layout: 'compact' | 'full';
  /** A pinned row sits on a tinted background, so its hover colour differs. */
  pinned?: boolean;
}

// One row of the notice board: date, category, title, file. With a file, the
// whole row is the link (a larger target, especially on a phone).
export function CircularItem({ circular, layout, pinned = false }: Props) {
  const { title, pdfUrl, fileSizeBytes, isNew, publishedAt, category } = circular;
  // "#" means the client has not sent the file yet; show the title as plain text
  // rather than a link that goes nowhere.
  const hasFile = pdfUrl !== '#';
  const full = layout === 'full';
  const size = fileSizeBytes ? formatSize(fileSizeBytes) : '';
  return (
    <li
      className={clsx(
        'relative flex flex-wrap items-baseline gap-x-5 gap-y-1 border-b border-rule',
        full ? 'px-6 py-[18px]' : 'py-4',
        hasFile && full && (pinned ? 'hover:bg-white' : 'hover:bg-paper'),
        // The link itself has no outline (it only covers the title text); the whole row shows the focus ring.
        hasFile && 'has-[a:focus-visible]:-outline-offset-3 has-[a:focus-visible]:outline-3 has-[a:focus-visible]:outline-teal-700',
      )}
    >
      <time dateTime={publishedAt} className="flex-none basis-[104px] font-mono text-[0.8125rem] text-muted">
        {formatDate(publishedAt)}
      </time>
      <span className="flex-none basis-[112px] font-mono text-[0.75rem] tracking-[0.06em] text-teal-700 uppercase">
        {CATEGORY_LABEL[category]}
      </span>
      {hasFile ? (
        <a
          href={pdfUrl}
          aria-label={`${title}${isNew ? ', new' : ''}, PDF${size ? `, ${size}` : ''}`}
          className={clsx(
            TITLE,
            'flex-[1_1_260px] text-ink underline underline-offset-[3px] hover:text-teal-700 focus-visible:outline-none',
            "after:absolute after:inset-0 after:content-['']",
          )}
        >
          {title}
          {isNew && <span className={clsx(NEW_BADGE, 'no-underline')}>New</span>}
        </a>
      ) : (
        <span className={clsx(TITLE, 'flex-[1_1_260px] text-ink')}>
          {title}
          {isNew && <span className={NEW_BADGE}>New</span>}
        </span>
      )}
      {/* Own line on a phone for every row, so rows with and without a file line up. */}
      <span className="basis-full font-mono text-[0.8125rem] text-muted sm:basis-auto">
        {hasFile ? `PDF${size ? ` · ${size}` : ''} ↓` : 'PDF soon'}
      </span>
    </li>
  );
}
